import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setError(null);
    } catch (err) {
      setUser(null);
      // Don't show error for 401 (not authenticated) - this is expected on login page
      // Only show error for actual failures (network errors, server errors)
      if (err.response?.status !== 401) {
        console.error('Auth check error:', err);
        // Don't set error message for initial auth check
        // setError('Authentication check failed');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Handle credential response (can be called from button component)
  const handleCredentialResponse = useCallback(async (response) => {
    const id_token = response?.credential;
    console.debug('handleCredentialResponse called', { hasCredential: !!id_token });
    if (!id_token) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/session`,
        { id_token },
        { withCredentials: true }
      );
      console.debug('session exchange response', res.data);
      // If backend returns an internal session token (useful when cookies are blocked), attach it
      if (res.data?.session_token) {
        const token = res.data.session_token;
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        console.debug('Attached session_token to axios defaults (masked)', token ? `${token.slice(0,6)}...${token.slice(-6)}` : null);
      }
      setUser(res.data);
      setError(null);
    } catch (err) {
      console.error('Session exchange failed:', err.response?.data || err);
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize Google Identity Services
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn('REACT_APP_GOOGLE_CLIENT_ID not set');
      return;
    }

    let container;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      try {
        // create hidden container for potential button rendering (not required for prompt)
        container = document.createElement('div');
        container.id = 'google-signin-button';
        container.style.display = 'none';
        document.body.appendChild(container);

        window.google?.accounts?.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse
        });
      } catch (e) {
        console.error('Failed to initialize Google Identity Services', e);
      }
    };

    document.body.appendChild(script);

    return () => {
      try {
        if (script && script.parentNode) script.parentNode.removeChild(script);
        if (container && container.parentNode) container.parentNode.removeChild(container);
      } catch (e) {}
    };
  }, [handleCredentialResponse]);

  const login = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setError('Google client ID not configured');
      return;
    }
    if (window.google?.accounts?.id) {
      // show Google One Tap / prompt
      window.google.accounts.id.prompt();
    } else {
      setError('Google auth not loaded');
    }
  };

  // Render a visible Google Sign-In button into a container element
  const renderGoogleButton = (containerId, options = {}) => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    if (!window.google?.accounts?.id) return;
    try {
      console.debug('renderGoogleButton', { containerId, options });
      window.google.accounts.id.renderButton(
        document.getElementById(containerId),
        Object.assign({
          theme: 'outline',
          size: 'large'
        }, options)
      );
    } catch (e) {
      console.error('Failed to render Google Sign-In button', e);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Remove Authorization header when logging out
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'super_admin' || user.role === 'developer') return true;
    return user.permissions?.includes(permission);
  };

  const isSuperAdmin = () => user?.role === 'super_admin';
  const isDeveloper = () => user?.role === 'developer';

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    hasPermission,
    isSuperAdmin,
    isDeveloper,
    renderGoogleButton,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
